import * as React from 'react';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import {Role} from "../../../types/userTypes";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import {useEffect, useState} from "react";
import {APP_PERMISSION, defaultRole} from "@/constants/userConstants";
import {IconButton, Stack} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import {getSubRoot} from "@/utils/utils";
import {useNavigate} from "react-router-dom";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import {deleteRole, getRoles} from "@/queries/userQueries";
import { circularIconHoverSx } from "@/styles/iconHover";
import {enqueueSnackbar} from "notistack";
import PermissionRendering from "@/pages/custom-components/UserComponents/PermissionRendering";

interface RoleDataGridProps {
}

export default function RoleDataGrid({ }: RoleDataGridProps) {
    const [openDialogDeleteRole, setOpenDialogRole] = useState<boolean>(false);
    const [selectedRole, setSelectedRole] = useState<Role>(JSON.parse(JSON.stringify(defaultRole)));
    const [roles, setRoles] = useState<Role[]>([]);
    const [rolesLoading, setRolesLoading] = useState<boolean>(false);

    const navigate = useNavigate()

    function onDeleteClick(role: Role) {
        setSelectedRole(role);
        setOpenDialogRole(true);
    }

    function onUpdateClick(role: Role) {
        navigate(`${getSubRoot(window.location.pathname)}/role/?id=${role.id}`);
    }

    function onCreateClick() {
        navigate(`${getSubRoot(window.location.pathname)}/role`);
    }

    async function handleDelete(){
        try{
            await deleteRole(selectedRole.id);
            setRoles(prev => prev.filter(r => r.id !== selectedRole.id))
            enqueueSnackbar("Role deleted successfully");
            setOpenDialogRole(false);
        }catch(err){
            console.error(err);
            setOpenDialogRole(false);
            enqueueSnackbar("Error deleting the role")
        }
    }

    async function loadingRoles() {
        try{
            setRolesLoading(true);
            const roles = await getRoles()
            setRoles(roles)
            setRolesLoading(false);
        }catch(e){
            console.error(e)
            enqueueSnackbar("Error retrieving roles.");
            setRolesLoading(false);
        }
    }

    useEffect(() => {
        loadingRoles()
    }, []);

    const columns: GridColDef<Role>[] = [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            minWidth: 150
        },
        {
            field: 'name',
            headerName: 'Nome',
            flex: 1,
            minWidth: 150
        },
        {
            field: 'description',
            headerName: 'Descrizione',
            flex: 2,
            minWidth: 250
        },
        {
            field: "actions",
            headerName: "",
            flex: 1,
            renderCell: (params) => (
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    spacing={1}
                    sx={{ width: '100%', height: '100%' }}
                >

                    <PermissionRendering permissions={[APP_PERMISSION.UPDATE_ROLE]}>
                        <IconButton
                            onClick={(e) => onUpdateClick(params.row)}
                            sx={circularIconHoverSx()}
                        >
                            <EditOutlinedIcon />
                        </IconButton>
                    </PermissionRendering>


                    <PermissionRendering permissions={[APP_PERMISSION.DELETE_ROLE]}>
                        <IconButton
                            onClick={(e) => onDeleteClick(params.row)}
                            sx={circularIconHoverSx()}
                        >
                            <DeleteOutlined />
                        </IconButton>
                    </PermissionRendering>

                </Stack>
            ),
        },
    ];

    return (
        <Box marginTop={3}>
            <Modal
                open={openDialogDeleteRole}
                onClose={() => setOpenDialogRole(false)}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1,
                    minWidth: 300
                }}>
                    <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                        Delete Role
                    </Typography>
                    <Typography sx={{ mb: 3 }}>
                        {`Are you sure you want to delete ${selectedRole.name}?`}
                    </Typography>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button variant="contained" onClick={() => setOpenDialogRole(false)}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={() => handleDelete()}>
                            Confirm
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <PermissionRendering permissions={[APP_PERMISSION.CREATE_ROLE]}>
                <Button variant="contained" startIcon={<AddCircleOutlineOutlinedIcon/>} onClick={onCreateClick}>
                    Create Role
                </Button>
            </PermissionRendering>

            <DataGridPro
                rows={roles}
                columns={columns}
                getRowId={(row) => row.id}
                loading={rolesLoading}
                pageSizeOptions={[5, 25, 50]}
                columnVisibilityModel={{
                    id: false
                }}
                sx={{
                    marginTop: 1
                }}
            />
        </Box>
    );
}